import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../config/api';
import { extractErrorMessage } from '../utils/extractError';

const PRIORITY_OPTIONS = [
  { value: 0, label: 'P0 — Emergency', color: '#ff4757' },
  { value: 1, label: 'P1 — High', color: '#ffa502' },
  { value: 2, label: 'P2 — Medium', color: '#ffa502' },
  { value: 3, label: 'P3 — Low', color: '#00e5a0' },
];

const EVENT_TYPE_OPTIONS = [
  { value: 'cash_handling', label: 'Cash Handling' },
  { value: 'custom', label: 'Custom' },
  { value: 'customer_incident', label: 'Customer Incident' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'employee_issue', label: 'Employee Issue' },
  { value: 'fuel_incident', label: 'Fuel Incident' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'robbery', label: 'Robbery' },
  { value: 'safety_hazard', label: 'Safety Hazard' },
  { value: 'shift_change', label: 'Shift Change' },
  { value: 'suspicious_activity', label: 'Suspicious Activity' },
  { value: 'theft_shoplifting', label: 'Theft / Shoplifting' },
];

const ACCEPTED_TYPES = '.jpg,.jpeg,.png,.mp4';
const MAX_FILES = 5;

interface FilePreview {
  file: File;
  previewUrl: string | null;
}

export default function CreateAlertPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [priority, setPriority] = useState<number>(2);
  const [eventType, setEventType] = useState('cash_handling');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [cameraLocation, setCameraLocation] = useState('');
  const [files, setFiles] = useState<FilePreview[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const storePath = `/stores/${storeId}`;

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files;
    if (!selected) return;

    const incoming = Array.from(selected);
    const total = files.length + incoming.length;
    if (total > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files allowed. You have ${files.length}, tried to add ${incoming.length}.`);
      return;
    }

    const newPreviews: FilePreview[] = incoming.map((file) => ({
      file,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));

    setFiles((prev) => [...prev, ...newPreviews]);
    setError('');

    // Reset input so re-selecting the same file works
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeFile(index: number) {
    setFiles((prev) => {
      const removed = prev[index];
      if (removed.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      setError('Title and description are required.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // 1. Upload each file
      const keys: string[] = [];
      for (const fp of files) {
        const formData = new FormData();
        formData.append('file', fp.file);
        const res = await api.post('/upload', formData);
        console.log('[Upload response]', fp.file.name, res.data);
        const body = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        keys.push(body.s3_key);
      }

      // 2. Create alert
      const alertPayload = {
        store_id: Number(storeId),
        priority,
        event_type: eventType,
        title: title.trim(),
        description: description.trim(),
        camera_location: cameraLocation.trim() || null,
        attachments: keys,
      };
      console.log('[Create alert payload]', alertPayload);
      await api.post('/alerts', alertPayload);

      // 3. Navigate back
      navigate(storePath);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to create alert.'));
    } finally {
      setSubmitting(false);
    }
  }

  const selectedPriority = PRIORITY_OPTIONS.find((p) => p.value === priority)!;

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <button onClick={() => navigate(storePath)} style={styles.backButton}>
          ← Back to Store
        </button>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Create Alert</h2>

          {error && <div style={styles.errorBanner}>{error}</div>}

          <form onSubmit={handleSubmit} style={styles.form}>
            {/* Priority & Event Type — side by side */}
            <div style={styles.row}>
              <div style={{ ...styles.fieldGroup, flex: 1 }}>
                <label style={styles.label}>Priority</label>
                <div style={{ position: 'relative' as const }}>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(Number(e.target.value))}
                    disabled={submitting}
                    style={{
                      ...styles.select,
                      borderLeft: `4px solid ${selectedPriority.color}`,
                    }}
                  >
                    {PRIORITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ ...styles.fieldGroup, flex: 1 }}>
                <label style={styles.label}>Event Type</label>
                <select
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  disabled={submitting}
                  style={styles.select}
                >
                  {EVENT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Title */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Brief summary of the incident"
                disabled={submitting}
                required
                style={styles.input}
              />
            </div>

            {/* Description */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Detailed description of what happened..."
                disabled={submitting}
                required
                rows={4}
                style={styles.textarea}
              />
            </div>

            {/* Camera Location */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Camera Location</label>
              <input
                type="text"
                value={cameraLocation}
                onChange={(e) => setCameraLocation(e.target.value)}
                placeholder='e.g. "Front door cam", "Register cam 2"'
                disabled={submitting}
                style={styles.input}
              />
            </div>

            {/* Attachments */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Attachments
                <span style={styles.labelHint}> (max {MAX_FILES} files — JPG, PNG, MP4)</span>
              </label>

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting || files.length >= MAX_FILES}
                style={{
                  ...styles.uploadButton,
                  opacity: files.length >= MAX_FILES ? 0.5 : 1,
                }}
              >
                Choose Files
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />

              {files.length > 0 && (
                <div style={styles.fileList}>
                  {files.map((fp, i) => (
                    <div key={i} style={styles.fileItem}>
                      {fp.previewUrl ? (
                        <img src={fp.previewUrl} alt={fp.file.name} style={styles.thumbnail} />
                      ) : (
                        <div style={styles.videoIcon}>▶</div>
                      )}
                      <span style={styles.fileName}>{fp.file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        disabled={submitting}
                        style={styles.removeFile}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={styles.actions}>
              <button
                type="button"
                onClick={() => navigate(storePath)}
                disabled={submitting}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button type="submit" disabled={submitting} style={styles.submitButton}>
                {submitting ? 'Submitting...' : 'Create Alert'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg-base)',
  },
  main: {
    maxWidth: 720,
    margin: '0 auto',
    padding: 'var(--space-8) var(--space-6)',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: 'var(--accent-text)',
    fontSize: 'var(--text-md)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    padding: 0,
    marginBottom: 'var(--space-6)',
  },
  card: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid var(--border-subtle)',
    padding: '32px 32px 28px',
  },
  cardTitle: {
    fontSize: 'var(--text-xl)',
    fontWeight: 600,
    fontFamily: 'var(--font-display)',
    color: 'var(--text-primary)',
    margin: '0 0 24px',
  },
  errorBanner: {
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
    padding: '12px 16px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-base)',
    fontFamily: 'var(--font-body)',
    border: '1px solid rgba(255,71,87,0.3)',
    marginBottom: 'var(--space-5)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-5)',
  },
  row: {
    display: 'flex',
    gap: 'var(--space-4)',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 'var(--text-base)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
  },
  labelHint: {
    fontWeight: 400,
    color: 'var(--text-tertiary)',
  },
  input: {
    padding: '10px 14px',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-default)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
    outline: 'none',
    background: 'var(--input-bg)',
    color: 'var(--text-primary)',
    colorScheme: 'dark',
    transition: 'border-color 0.2s',
  },
  textarea: {
    padding: '10px 14px',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-default)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
    outline: 'none',
    resize: 'vertical',
    background: 'var(--input-bg)',
    color: 'var(--text-primary)',
    colorScheme: 'dark',
    transition: 'border-color 0.2s',
  },
  select: {
    padding: '10px 14px',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-default)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
    outline: 'none',
    background: 'var(--input-bg)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    appearance: 'auto' as any,
    colorScheme: 'dark',
    transition: 'border-color 0.2s',
  },
  uploadButton: {
    alignSelf: 'flex-start',
    padding: '12px 20px',
    borderRadius: 'var(--radius-lg)',
    border: '1px dashed var(--border-default)',
    background: 'transparent',
    fontSize: 'var(--text-base)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-tertiary)',
    cursor: 'pointer',
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 4,
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    background: 'var(--bg-surface-raised)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-subtle)',
  },
  thumbnail: {
    width: 40,
    height: 40,
    objectFit: 'cover',
    borderRadius: 'var(--radius-sm)',
  },
  videoIcon: {
    width: 40,
    height: 40,
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-surface-overlay)',
    color: 'var(--text-primary)',
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileName: {
    flex: 1,
    fontSize: 'var(--text-base)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  removeFile: {
    background: 'none',
    border: 'none',
    color: 'var(--text-tertiary)',
    fontSize: 14,
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: 'var(--radius-sm)',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    padding: '10px 22px',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-surface-raised)',
    fontSize: 'var(--text-md)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
  },
  submitButton: {
    padding: '10px 24px',
    borderRadius: 'var(--radius-lg)',
    border: 'none',
    background: 'var(--status-critical)',
    color: '#fff',
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
  },
};
